import React from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { TNaerasDashboardPumping, TNaerasPanel } from '@/data/types';
import PanelCard, { Kpi } from './PanelCard';
import { COLORS, MISSING, hourLabel, isNum, m3, mwh, num } from '../utils/format';

interface PumpingPanelProps {
  panel: TNaerasPanel<TNaerasDashboardPumping> | undefined;
  splitHour: number | null;
}

const YESTERDAY = COLORS.neutral;

const HourTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  const point = payload[0].payload;
  const rows = [
    { label: 'Μέτρηση', value: point.measured },
    { label: 'Εκτίμηση', value: point.estimated },
    { label: 'Χθες', value: point.yesterday },
  ].filter((row) => isNum(row.value));

  return (
    <div className="rounded-2xl border border-slate-100 bg-white/95 p-3 shadow-xl backdrop-blur-md">
      <p className="mb-1 text-xs font-bold text-slate-800">{hourLabel(Number(label))}</p>
      {rows.map((row) => (
        <div key={row.label} className="flex items-center gap-2 text-xs">
          <span className="font-medium text-slate-600">{row.label}:</span>
          <span className="font-extrabold text-slate-900">{num(row.value, 0)} m³/h</span>
        </div>
      ))}
    </div>
  );
};

/**
 * §2.6 — hourly pumping-station flow: measured up to the split hour, estimated
 * from the wind forecast after it, yesterday as a thin line for comparison.
 * The two series never share an hour, so one bar per hour is drawn.
 */
export const PumpingPanel: React.FC<PumpingPanelProps> = ({ panel, splitHour }) => {
  const split = splitHour !== null ? hourLabel(splitHour) : MISSING;

  return (
    <PanelCard title="Ημερήσια Άντληση" subtitle="Ωριαία παροχή αντλιοστασίου (m³/h)" panel={panel} delay={0.5}>
      {(pumping) => {
        const points = Array.from({ length: 24 }, (_, hour) => ({
          hour,
          measured: pumping.measured_m3h[hour] ?? null,
          estimated: pumping.estimated_m3h[hour] ?? null,
          yesterday: pumping.yesterday_m3h?.[hour] ?? null,
        }));

        return (
          <>
            <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-2">
              <Kpi
                label={`Μέτρηση 00–${split}`}
                value={m3(pumping.real_m3)}
                sub={mwh(pumping.real_mwh)}
                tone="hydro"
              />
              <Kpi
                label={`Εκτίμηση ${split}–24`}
                value={m3(pumping.est_m3)}
                sub={mwh(pumping.est_mwh)}
                tone="hydro"
              />
              <Kpi label="Σύνολο" value={m3(pumping.total_m3)} sub={mwh(pumping.total_mwh)} tone="hydro" />
              <Kpi label="Χθες" value={m3(pumping.yesterday_m3)} />
            </div>

            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={points} margin={{ top: 16, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <pattern id="pumpingEstimate" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                      <rect width="6" height="6" fill={COLORS.hydroEst} />
                      <line x1="0" y1="0" x2="0" y2="6" stroke={COLORS.hydro} strokeWidth="2" />
                    </pattern>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis
                    dataKey="hour"
                    axisLine={false}
                    tickLine={false}
                    interval={2}
                    tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
                    tickFormatter={(h) => `${h}:00`}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
                    tickFormatter={(v) => num(v, 0)}
                    width={48}
                  />
                  <Tooltip content={<HourTooltip />} cursor={{ fill: 'rgba(241, 245, 249, 0.6)' }} />
                  <Bar dataKey="measured" stackId="flow" fill={COLORS.hydro} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="estimated" stackId="flow" fill="url(#pumpingEstimate)" radius={[4, 4, 0, 0]} />
                  <Line
                    dataKey="yesterday"
                    stroke={YESTERDAY}
                    strokeWidth={1.5}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  {splitHour !== null && splitHour < 24 && (
                    <ReferenceLine
                      x={splitHour}
                      position="start"
                      stroke="#475569"
                      strokeDasharray="4 3"
                      label={{ value: split, position: 'top', fill: '#475569', fontSize: 10, fontWeight: 700 }}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-slate-100 pt-3 text-[11px] font-semibold text-slate-500">
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: COLORS.hydro }} />
                Μέτρηση
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: COLORS.hydroEst }} />
                Εκτίμηση
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-0.5 w-4" style={{ backgroundColor: YESTERDAY }} />
                Χθες
              </span>
            </div>
          </>
        );
      }}
    </PanelCard>
  );
};

export default PumpingPanel;
